import { Component, AfterViewInit, Input, OnDestroy } from '@angular/core';

declare let Bokeh: any;

interface BarConfig {
  start: number;
  end: number;
  time: string;
  value?: number;
  unit?: string;
  vo2tot?: number;
}

interface DataConfig {
  duration: number[];
  lactate_values: number[];
  vo2_values: number[];
  vo2_dots_x?: number[];
  vo2_dots_y?: number[];
  lactate_dots_x?: number[];
  lactate_dots_y?: number[];
  lactate_dots_error?: number[][];
  vo2_dots_error?: number[][];
  bars: BarConfig[];
  chart_height?: number;
  element_suffix?: string;
}

interface ThemeLactate {
  line: string;
  dots: string;
  axis: string;
  error: string;
}

interface ThemeVO2 {
  line: string;
  dots: string;
  axis: string;
  error: string;
}

interface ThemeBars {
  fill: string;
  fillAlpha: number;
  stroke: string;
  strokeAlpha: number;
  text: string;
  timeLabel: string;
}

interface ChartTheme {
  background: string;
  plotBackground: string;
  gridLine: string;
  gridLineAlpha: number;
  border: string;
  lactate: ThemeLactate;
  vo2: ThemeVO2;
  bars: ThemeBars;
}

interface ChartDimensions {
  x: [number, number];
  l: [number, number];
  v: [number, number];
}

@Component({
  selector: 'app-kinetics-graph',
  templateUrl: './kinetics-graph.component.html',
  styleUrls: ['./kinetics-graph.component.scss'],
})
export class KineticsGraphComponent implements AfterViewInit, OnDestroy {
  @Input() public config: DataConfig[] = [];

  private chart: any;
  private readonly currentTheme: string;
  private theme: ChartTheme = {} as ChartTheme;

  private readonly CHART_HEIGHT: number = 500;
  private readonly PADDING_MULTIPLIER: number = 0.05;
  private readonly RANGE_MULTIPLIER: number = 0.15;
  private readonly LACTATE_STEP: number = 2;
  private readonly MIN_LACTATE_RANGE: number = 6;
  private readonly ERROR_BAR_CAP_RATIO: number = 0.2;
  private readonly TIME_LABEL_Y_POSITION: number = 0.02;
  private readonly DOT_RADIUS_PIXELS: number = 1;

  private readonly themes: Record<string, ChartTheme> = {
    light: {
      background: '#fbfbfb',
      plotBackground: '#fbfbfb',
      gridLine: '#e0e0e0',
      gridLineAlpha: 0.8,
      border: '#cccccc',
      lactate: {
        line: '#ff0000',
        dots: '#ff0000',
        axis: '#000000',
        error: '#ff0000',
      },
      vo2: {
        line: '#0000ff',
        dots: '#0000ff',
        axis: '#000000',
        error: '#0000ff',
      },
      bars: {
        fill: '#d3d3d3',
        fillAlpha: 0.3,
        stroke: '#808080',
        strokeAlpha: 0.5,
        text: '#000000',
        timeLabel: '#000000',
      },
    },
    dark: {
      background: '#282929',
      plotBackground: '#111111',
      gridLine: '#555555',
      gridLineAlpha: 0.6,
      border: '#666666',
      lactate: {
        line: '#ff6b6b',
        dots: '#ff6b6b',
        axis: '#aaaaaa',
        error: '#ff6b6b',
      },
      vo2: {
        line: '#4dabf7',
        dots: '#4dabf7',
        axis: '#aaaaaa',
        error: '#4dabf7',
      },
      bars: {
        fill: '#404040',
        fillAlpha: 0.4,
        stroke: '#707070',
        strokeAlpha: 0.7,
        text: '#ffffff',
        timeLabel: '#cccccc',
      },
    },
  };

  constructor() {
    this.currentTheme = (localStorage.getItem('theme') || 'light').toLowerCase();
  }

  public ngAfterViewInit(): void {
    if (this.config?.[0]) {
      this.chart = this.createChart(this.config[0]);
      const suffix = this.config[0].element_suffix;
      const elementId = suffix ? '#kinetics-graph' + `-${suffix}` : '#kinetics-graph';
      Bokeh.Plotting.show(this.chart, elementId);
    }
  }

  public ngOnDestroy(): void {
    this.chart = null;
  }

  private get getCurrentTheme(): ChartTheme {
    return this.themes[this.currentTheme];
  }

  private createChart(data: DataConfig): any {
    this.theme = this.getCurrentTheme;

    const plot: any = new Bokeh.Plot({
      sizing_mode: 'stretch_width',
      height: data.chart_height ?? this.CHART_HEIGHT,
      title: null,
      toolbar_location: null,
      margin: [0, 0, 0, 0],
      background_fill_color: this.theme.plotBackground,
      border_fill_color: this.theme.background,
      outline_line_color: this.theme.gridLine,
      outline_line_alpha: this.theme.gridLineAlpha,
    });

    const dimensions: ChartDimensions = this.getDimensions(data);
    this.setupAxes(plot, dimensions);

    const renderers: any[] = [
      ...this.createBars(data, dimensions),
      ...this.createVO2(data, dimensions),
      ...this.createLactate(data, dimensions),
    ];

    plot.renderers = plot.renderers.concat(renderers);
    return plot;
  }

  private getDimensions(data: DataConfig): ChartDimensions {
    const xValues: number[] = [...data.duration, ...data.bars.flatMap((bar: BarConfig) => [bar.start, bar.end])];
    const lactateValues: number[] = [...data.lactate_values];
    const vo2Values: number[] = [
      ...data.vo2_values,
      ...data.bars.filter((bar: BarConfig) => bar.vo2tot).map((bar: BarConfig) => bar.vo2tot!),
    ];

    if (data.lactate_dots_y) {
      lactateValues.push(...data.lactate_dots_y);
    }

    if (data.vo2_dots_y) {
      vo2Values.push(...data.vo2_dots_y);
    }

    this.addLactateErrorBarsToRange(data, lactateValues);
    this.addVO2ErrorBarsToRange(data, vo2Values);

    const xMin: number = Math.min(...xValues);
    const xMax: number = Math.max(...xValues);
    const lactateMax: number = Math.max(...lactateValues);
    const vo2Max: number = Math.max(...vo2Values);

    const xPadding: number = (xMax - xMin) * this.PADDING_MULTIPLIER;
    const lactateMaxRounded: number = Math.ceil(lactateMax / this.LACTATE_STEP) * this.LACTATE_STEP;
    const lactateEndValue: number = Math.max(lactateMaxRounded, this.MIN_LACTATE_RANGE);

    return {
      x: [xMin - xPadding, xMax + xPadding],
      l: [0, lactateEndValue],
      v: [0, vo2Max + vo2Max * this.RANGE_MULTIPLIER],
    };
  }

  private addLactateErrorBarsToRange(data: DataConfig, lactateValues: number[]): void {
    if (!data.lactate_dots_x || !data.lactate_dots_y || !data.lactate_dots_error) {
      return;
    }

    for (let i = 0; i < data.lactate_dots_y.length; i++) {
      const y: number = data.lactate_dots_y[i];
      if (data.lactate_dots_error[i]) {
        const [errUp = 0, errDown = 0] = data.lactate_dots_error[i].map(Math.abs);
        lactateValues.push(y + errUp, y - errDown);
      }
    }
  }

  private addVO2ErrorBarsToRange(data: DataConfig, vo2Values: number[]): void {
    if (!data.vo2_dots_x || !data.vo2_dots_y || !data.vo2_dots_error) {
      return;
    }

    for (let i = 0; i < data.vo2_dots_y.length; i++) {
      const y: number = data.vo2_dots_y[i];
      if (data.vo2_dots_error[i]) {
        const [errUp = 0, errDown = 0] = data.vo2_dots_error[i].map(Math.abs);
        vo2Values.push(y + errUp, y - errDown);
      }
    }
  }

  private setupAxes(plot: any, dimensions: ChartDimensions): void {
    const xTicker: any = this.createXAxisTicker();
    const xFormatter: any = this.createTimeFormatter();
    const lactateTicker: any = this.createLactateTicker(dimensions.l[1]);
    const vo2Ticker: any = this.createVO2Ticker();

    const xAxis: any = this.createXAxis(xTicker, xFormatter);
    const yLeftAxis: any = this.createLeftYAxis(lactateTicker);
    const yRightAxis: any = this.createRightYAxis(vo2Ticker);

    plot.add_layout(xAxis, 'below');
    plot.add_layout(yLeftAxis, 'left');
    plot.add_layout(yRightAxis, 'right');

    this.addGridLines(plot, xTicker, lactateTicker);
    this.setupRanges(plot, dimensions, yRightAxis);
  }

  private createXAxisTicker(): any {
    return new Bokeh.BasicTicker({
      desired_num_ticks: 8,
    });
  }

  private createTimeFormatter(): any {
    const formatter: any = new Bokeh.PrintfTickFormatter({
      format: '%d',
    });

    formatter.doFormat = (ticks: number[]): string[] => {
      return ticks.map((tick: number) => this.formatTimeLabel(tick));
    };

    return formatter;
  }

  private formatTimeLabel(seconds: number): string {
    const minutes: number = Math.floor(seconds / 60);
    const remainingSeconds: number = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  private createLactateTicker(maxValue: number): any {
    const lactateTicks: number[] = [];
    for (let tick = 0; tick <= maxValue; tick += this.LACTATE_STEP) {
      lactateTicks.push(tick);
    }
    return new Bokeh.FixedTicker({ ticks: lactateTicks });
  }

  private createVO2Ticker(): any {
    return new Bokeh.BasicTicker({
      desired_num_ticks: 6,
    });
  }

  private createXAxis(ticker: any, formatter: any): any {
    return new Bokeh.LinearAxis({
      axis_label: 'Duration [mm:ss]',
      axis_label_text_font_size: '10pt',
      axis_label_text_font_style: 'normal',
      major_label_text_font_size: '8pt',
      axis_label_text_color: this.theme.bars.text,
      major_label_text_color: this.theme.bars.text,
      axis_line_color: '#000000',
      major_tick_line_color: '#000000',
      minor_tick_line_color: '#000000',
      ticker: ticker,
      formatter: formatter,
    });
  }

  private createLeftYAxis(ticker: any): any {
    return new Bokeh.LinearAxis({
      axis_label: 'Lactate [mmol/l]',
      axis_label_text_font_style: 'normal',
      axis_label_text_color: this.theme.lactate.axis,
      axis_label_text_font_size: '10pt',
      major_label_text_color: this.theme.lactate.axis,
      major_label_text_font_size: '8pt',
      axis_line_color: '#000000',
      major_tick_line_color: '#000000',
      minor_tick_line_color: '#000000',
      ticker: ticker,
    });
  }

  private createRightYAxis(ticker: any): any {
    return new Bokeh.LinearAxis({
      axis_label: 'VO2 [ml/min/kg]',
      axis_label_text_font_style: 'normal',
      axis_label_text_color: this.theme.vo2.axis,
      axis_label_text_font_size: '10pt',
      major_label_text_color: this.theme.vo2.axis,
      major_label_text_font_size: '8pt',
      axis_line_color: '#000000',
      major_tick_line_color: '#000000',
      minor_tick_line_color: '#000000',
      ticker: ticker,
    });
  }

  private addGridLines(plot: any, xTicker: any, lactateTicker: any): void {
    const xGrid: any = new Bokeh.Grid({
      dimension: 0,
      ticker: xTicker,
      grid_line_color: this.theme.gridLine,
      grid_line_alpha: this.theme.gridLineAlpha,
    });

    const yGrid: any = new Bokeh.Grid({
      dimension: 1,
      ticker: lactateTicker,
      grid_line_color: this.theme.gridLine,
      grid_line_alpha: this.theme.gridLineAlpha,
    });

    plot.add_layout(xGrid);
    plot.add_layout(yGrid);
  }

  private setupRanges(plot: any, dimensions: ChartDimensions, yRightAxis: any): void {
    plot.x_range = new Bokeh.Range1d({ start: dimensions.x[0], end: dimensions.x[1] });
    plot.y_range = new Bokeh.Range1d({ start: dimensions.l[0], end: dimensions.l[1] });

    const vo2Range: any = new Bokeh.Range1d({ start: dimensions.v[0], end: dimensions.v[1] });
    plot.extra_y_ranges = { vo2: vo2Range };

    yRightAxis.y_range_name = 'vo2';
  }

  private createBars(data: DataConfig, dimensions: ChartDimensions): any[] {
    return data.bars.flatMap((bar: BarConfig) => {
      const barX: number = (bar.start + bar.end) / 2;
      const barHeight: number = bar.vo2tot ? (bar.vo2tot / dimensions.v[1]) * dimensions.l[1] : 1;
      const barY: number = barHeight / 2;

      const rect: any = this.createBarRect(barX, barY, bar.end - bar.start, barHeight);
      const timeLabel: any = this.createTimeLabel(barX, dimensions.l[1]);

      const renderers: any[] = [rect, timeLabel];

      if (bar.value && bar.unit) {
        const valueLabel: any = this.createValueLabel(barX, barY, bar.value, bar.unit);
        renderers.push(valueLabel);
      }

      return renderers;
    });
  }

  private calculateDotRadius(dimensions: ChartDimensions): number {
    const xRange: number = dimensions.x[1] - dimensions.x[0];
    return (xRange / this.CHART_HEIGHT) * this.DOT_RADIUS_PIXELS;
  }

  private createVO2(data: DataConfig, dimensions: ChartDimensions): any[] {
    const line: any = this.createVO2MainLine(data.duration, data.vo2_values);
    const renderers: any[] = [line];

    if (data.vo2_dots_x?.length) {
      this.addVO2DotsWithErrors(data, renderers, dimensions);
    }

    return renderers;
  }

  private createBarRect(x: number, y: number, width: number, height: number): any {
    return new Bokeh.GlyphRenderer({
      data_source: new Bokeh.ColumnDataSource({
        data: { x: [x], y: [y], width: [width], height: [height] },
      }),
      glyph: new Bokeh.Rect({
        x: { field: 'x' },
        y: { field: 'y' },
        width: { field: 'width' },
        height: { field: 'height' },
        fill_color: this.theme.bars.fill,
        fill_alpha: this.theme.bars.fillAlpha,
        line_color: this.theme.bars.stroke,
        line_width: 1,
        line_alpha: this.theme.bars.strokeAlpha,
      }),
    });
  }

  private createTimeLabel(x: number, maxLactate: number): any {
    return new Bokeh.GlyphRenderer({
      data_source: new Bokeh.ColumnDataSource({
        data: { x: [x], y: [maxLactate * this.TIME_LABEL_Y_POSITION], text: [''] },
      }),
      glyph: new Bokeh.Text({
        x: { field: 'x' },
        y: { field: 'y' },
        text: { field: 'text' },
        text_align: 'center',
        text_baseline: 'middle',
        text_font_size: '9pt',
        text_color: this.theme.bars.timeLabel,
      }),
    });
  }

  private createValueLabel(x: number, y: number, value: number, unit: string): any {
    return new Bokeh.GlyphRenderer({
      data_source: new Bokeh.ColumnDataSource({
        data: { x: [x], y: [y], text: [`${value} ${unit}`] },
      }),
      glyph: new Bokeh.Text({
        x: { field: 'x' },
        y: { field: 'y' },
        text: { field: 'text' },
        text_align: 'center',
        text_baseline: 'middle',
        text_font_size: '10pt',
        text_color: this.theme.bars.text,
      }),
    });
  }

  private createVO2MainLine(xValues: number[], yValues: number[]): any {
    return new Bokeh.GlyphRenderer({
      data_source: new Bokeh.ColumnDataSource({ data: { x: xValues, y: yValues } }),
      glyph: new Bokeh.Line({
        x: { field: 'x' },
        y: { field: 'y' },
        line_width: 2,
        line_color: this.theme.vo2.line,
        line_alpha: 0.8,
      }),
      y_range_name: 'vo2',
    });
  }

  private addVO2DotsWithErrors(data: DataConfig, renderers: any[], dimensions: ChartDimensions): void {
    const dotRadius: number = this.calculateDotRadius(dimensions);

    for (let i = 0; i < data.vo2_dots_x!.length; i++) {
      const x: number = data.vo2_dots_x![i];
      const y: number = data.vo2_dots_y![i];

      if (data.vo2_dots_error?.[i]) {
        this.addVO2ErrorBars(x, y, data.vo2_dots_error[i], renderers);
      }

      const dot: any = this.createVO2Dot(x, y, dotRadius);
      renderers.push(dot);
    }
  }

  private addVO2ErrorBars(x: number, y: number, errors: number[], renderers: any[]): void {
    const [errUp = 0, errDown = 0, errHoriz = 0] = errors.map(Math.abs);

    if (errUp) {
      renderers.push(
        this.createVO2Line([x, x], [y, y + errUp]),
        this.createVO2Line([x - errHoriz / 2, x + errHoriz / 2], [y + errUp, y + errUp])
      );
    }

    if (errDown) {
      renderers.push(
        this.createVO2Line([x, x], [y, y - errDown]),
        this.createVO2Line([x - errHoriz / 2, x + errHoriz / 2], [y - errDown, y - errDown])
      );
    }

    if (errHoriz) {
      const capHeight: number = Math.max(errUp, errDown) * this.ERROR_BAR_CAP_RATIO;
      renderers.push(
        this.createVO2Line([x - errHoriz, x + errHoriz], [y, y]),
        this.createVO2Line([x - errHoriz, x - errHoriz], [y - capHeight, y + capHeight]),
        this.createVO2Line([x + errHoriz, x + errHoriz], [y - capHeight, y + capHeight])
      );
    }
  }

  private createVO2Dot(x: number, y: number, radius: number): any {
    return new Bokeh.GlyphRenderer({
      data_source: new Bokeh.ColumnDataSource({ data: { x: [x], y: [y] } }),
      glyph: new Bokeh.Circle({
        x: { field: 'x' },
        y: { field: 'y' },
        radius: radius,
        fill_color: this.theme.vo2.dots,
        line_color: this.theme.vo2.dots,
        fill_alpha: 0.7,
        line_alpha: 0.7,
      }),
      y_range_name: 'vo2',
    });
  }

  private createVO2Line(xValues: number[], yValues: number[]): any {
    return new Bokeh.GlyphRenderer({
      data_source: new Bokeh.ColumnDataSource({ data: { x: xValues, y: yValues } }),
      glyph: new Bokeh.Line({
        x: { field: 'x' },
        y: { field: 'y' },
        line_width: 2,
        line_color: this.theme.vo2.error,
      }),
      y_range_name: 'vo2',
    });
  }

  private createLactate(data: DataConfig, dimensions: ChartDimensions): any[] {
    const line: any = this.createLactateMainLine(data.duration, data.lactate_values);
    const renderers: any[] = [line];

    if (data.lactate_dots_x?.length) {
      this.addLactateDotsWithErrors(data, renderers, dimensions);
    }

    return renderers;
  }

  private createLactateMainLine(xValues: number[], yValues: number[]): any {
    return new Bokeh.GlyphRenderer({
      data_source: new Bokeh.ColumnDataSource({ data: { x: xValues, y: yValues } }),
      glyph: new Bokeh.Line({
        x: { field: 'x' },
        y: { field: 'y' },
        line_width: 2,
        line_color: this.theme.lactate.line,
        line_alpha: 0.8,
      }),
    });
  }

  private addLactateDotsWithErrors(data: DataConfig, renderers: any[], dimensions: ChartDimensions): void {
    const dotRadius: number = this.calculateDotRadius(dimensions);

    for (let i = 0; i < data.lactate_dots_x!.length; i++) {
      const x: number = data.lactate_dots_x![i];
      const y: number = data.lactate_dots_y![i];

      if (data.lactate_dots_error?.[i]) {
        this.addLactateErrorBars(x, y, data.lactate_dots_error[i], renderers);
      }

      const dot: any = this.createLactateDot(x, y, dotRadius);
      renderers.push(dot);
    }
  }

  private addLactateErrorBars(x: number, y: number, errors: number[], renderers: any[]): void {
    const [errUp = 0, errDown = 0, errHoriz = 0] = errors.map(Math.abs);

    if (errUp) {
      renderers.push(
        this.createLactateLine([x, x], [y, y + errUp]),
        this.createLactateLine([x - errHoriz / 2, x + errHoriz / 2], [y + errUp, y + errUp])
      );
    }

    if (errDown) {
      renderers.push(
        this.createLactateLine([x, x], [y, y - errDown]),
        this.createLactateLine([x - errHoriz / 2, x + errHoriz / 2], [y - errDown, y - errDown])
      );
    }

    if (errHoriz) {
      const capHeight: number = Math.max(errUp, errDown) * this.ERROR_BAR_CAP_RATIO;
      renderers.push(
        this.createLactateLine([x - errHoriz, x + errHoriz], [y, y]),
        this.createLactateLine([x - errHoriz, x - errHoriz], [y - capHeight, y + capHeight]),
        this.createLactateLine([x + errHoriz, x + errHoriz], [y - capHeight, y + capHeight])
      );
    }
  }

  private createLactateDot(x: number, y: number, radius: number): any {
    return new Bokeh.GlyphRenderer({
      data_source: new Bokeh.ColumnDataSource({ data: { x: [x], y: [y] } }),
      glyph: new Bokeh.Circle({
        x: { field: 'x' },
        y: { field: 'y' },
        radius: radius,
        fill_color: this.theme.lactate.dots,
        line_color: this.theme.lactate.dots,
        fill_alpha: 0.9,
        line_alpha: 0.9,
      }),
    });
  }

  private createLactateLine(xValues: number[], yValues: number[]): any {
    return new Bokeh.GlyphRenderer({
      data_source: new Bokeh.ColumnDataSource({ data: { x: xValues, y: yValues } }),
      glyph: new Bokeh.Line({
        x: { field: 'x' },
        y: { field: 'y' },
        line_width: 2,
        line_color: this.theme.lactate.error,
      }),
    });
  }
}
